import { useState } from 'react';
import type { ChecklistItem } from '../types';
import * as templatesApi from '../api/templates';

interface Props {
  teamId: string;
  taskId: string;
  items: ChecklistItem[];
  /**
   * Called after we mutate. Receives the updated task (from the backend
   * response) so the parent can upsert into its own list.
   */
  onTaskUpdate: (task: {
    checklist?: ChecklistItem[];
    status?: string;
    completedAt?: string;
  }) => void;
  /**
   * Structural edit (add/remove/relabel) — parent PATCHes the whole task.
   * Accepts the new checklist array.
   */
  onReplaceChecklist: (next: ChecklistItem[]) => Promise<void>;
}

/**
 * ADR 0010 per-task checklist UI. Single-checkbox toggles hit the
 * dedicated `PATCH /tasks/:id/checklist/:itemId` endpoint so a tick is one
 * round-trip. Structural edits (add, remove, relabel) PATCH the full task
 * via the caller's `onReplaceChecklist` to keep the existing `updateTask`
 * path authoritative for schema changes.
 */
export function TaskChecklist({
  teamId,
  taskId,
  items,
  onTaskUpdate,
  onReplaceChecklist,
}: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [newDraft, setNewDraft] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  async function toggle(item: ChecklistItem) {
    setBusyId(item.id);
    try {
      const task = await templatesApi.toggleChecklistItem(
        teamId,
        taskId,
        item.id,
        !item.done
      );
      onTaskUpdate(task);
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(item: ChecklistItem) {
    setEditingId(item.id);
    setEditDraft(item.label);
  }

  async function commitEdit(item: ChecklistItem) {
    const label = editDraft.trim();
    if (!label) {
      setEditingId(null);
      return;
    }
    if (label === item.label) {
      setEditingId(null);
      return;
    }
    const next = items.map((i) =>
      i.id === item.id ? { ...i, label } : i
    );
    setEditingId(null);
    await onReplaceChecklist(next);
  }

  async function addItem() {
    const label = newDraft.trim();
    if (!label) return;
    const uuid =
      globalThis.crypto?.randomUUID?.() ??
      `local-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    const next: ChecklistItem[] = [
      ...items,
      { id: uuid, label, done: false },
    ];
    setNewDraft('');
    await onReplaceChecklist(next);
  }

  async function removeItem(item: ChecklistItem) {
    const next = items.filter((i) => i.id !== item.id);
    await onReplaceChecklist(next);
  }

  return (
    <section className="task-checklist" aria-label="Checklist">
      <div className="task-checklist-header">
        <span>
          Checklist {done}/{total} · {percent}%
        </span>
        <span className="task-checklist-progress-bar" aria-hidden="true">
          <span
            className="task-checklist-progress-fill"
            style={{ width: `${percent}%` }}
          />
        </span>
      </div>

      <ul className="task-checklist-list">
        {items.map((item) => {
          const isEditing = editingId === item.id;
          return (
            <li
              key={item.id}
              className={`task-checklist-item ${item.done ? 'is-done' : ''}`}
            >
              <input
                type="checkbox"
                checked={item.done}
                disabled={busyId === item.id}
                onChange={() => void toggle(item)}
                aria-label={`Toggle ${item.label}`}
              />
              {isEditing ? (
                <input
                  className="task-checklist-item-edit"
                  type="text"
                  value={editDraft}
                  autoFocus
                  onChange={(e) => setEditDraft(e.target.value)}
                  onBlur={() => void commitEdit(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') {
                      setEditingId(null);
                    }
                  }}
                />
              ) : (
                <span
                  className="task-checklist-item-label"
                  onClick={() => startEdit(item)}
                  title="Click to edit"
                >
                  {item.label}
                </span>
              )}
              <button
                type="button"
                className="task-checklist-item-remove"
                onClick={() => void removeItem(item)}
                aria-label={`Remove ${item.label}`}
                title="Remove item"
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>

      <div className="task-checklist-add-row">
        <input
          type="text"
          value={newDraft}
          onChange={(e) => setNewDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addItem();
            }
          }}
          placeholder="Add an item…"
        />
        <button
          type="button"
          onClick={() => void addItem()}
          disabled={!newDraft.trim()}
        >
          + Add
        </button>
      </div>
    </section>
  );
}
